import styled from "@emotion/styled";
import { type OpportunityStatusEnum, StagesEnum } from "@repo/api";
import { LightMode, Text, Variables } from "@repo/skapa";
import { translateStage } from "@repo/ui";

import { isClosedLostOrInvalidStatus } from "./utils";

type StageStatus = "ACTIVE" | "COMPLETED" | "INACTIVE" | "LOST";

interface StageItemProps {
  stage: StagesEnum;
  index: number;
  currentStep: number;
  salesClosedStep: number;
  currentStatus?: OpportunityStatusEnum;
  isFirst?: boolean;
  isLast?: boolean;
  onClick?: () => void;
}

interface StageProps {
  isFirst?: boolean;
  isLast?: boolean;
  status: StageStatus;
}

const getStepStatus = (
  step: number,
  currentStep: number
): Exclude<StageStatus, "LOST"> => {
  if (step < currentStep) {
    return "COMPLETED";
  }
  if (step === currentStep) {
    return "ACTIVE";
  }
  return "INACTIVE";
};

const getStageStatus = ({
  index,
  stage,
  currentStatus,
  salesClosedStep,
  currentStep,
}: {
  index: number;
  stage: StagesEnum;
  currentStatus?: OpportunityStatusEnum;
  salesClosedStep: number;
  currentStep: number;
}) => {
  const step = index + 1;
  const isClosedOrInvalid = isClosedLostOrInvalidStatus(currentStatus);

  const isSalesClosedLossStage =
    isClosedOrInvalid &&
    stage === StagesEnum.SALES_CLOSED &&
    salesClosedStep > 0 &&
    currentStep >= salesClosedStep;

  const stageStatus: StageStatus = isSalesClosedLossStage
    ? "LOST"
    : getStepStatus(step, currentStep);

  return stageStatus;
};

const getBackgroundColor = (status: StageStatus) => {
  switch (status) {
    case "ACTIVE":
      return LightMode.colourStaticIkeaBrandBlue;
    case "COMPLETED":
      return LightMode.colourExtendedPalette17;
    case "INACTIVE":
      return LightMode.colourNeutral3;
    case "LOST":
      return LightMode.colourSemanticNegative;

    default:
      return LightMode.colourNeutral3;
  }
};

const getTextColor = (status: StageStatus) => {
  switch (status) {
    case "ACTIVE":
      return LightMode.colourTextAndIcon5;
    case "LOST":
      return LightMode.colourTextAndIcon5;
    case "COMPLETED":
      return LightMode.colourTextAndIcon1;
    case "INACTIVE":
      return LightMode.colourTextAndIcon1;
    default:
      return LightMode.colourTextAndIcon1;
  }
};

const StageListItem = styled.li`
  display: flex;
  flex-grow: 1;
  flex-basis: 0;
  min-width: 120px;
  flex-shrink: 0;
  position: relative;
  overflow: visible;
`;

const Stage = styled.button<StageProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  position: relative;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;

  &:hover:not(:disabled) {
    filter: brightness(0.95);
  }

  &:disabled {
    cursor: default;
  }
`;

const Connector = styled.span<StageProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  background: ${({ status }) => getBackgroundColor(status)};
  padding: ${Variables.space50}
    calc(${Variables.space75} + ${Variables.space100});

  clip-path: ${({ isFirst, isLast }) => {
    if (isFirst) {
      return `polygon(
        0 0,
        calc(100% - ${Variables.space100}) 0,
        100% 50%,
        calc(100% - ${Variables.space100}) 100%,
        0 100%
      )`;
    } else if (isLast) {
      return `polygon(
        0 0,
        100% 0,
        100% 100%,
      0 100%,
        ${Variables.space100} 50%
      )`;
    } else {
      return `polygon(
        0 0,
        calc(100% - ${Variables.space100}) 0,
        100% 50%,
        calc(100% - ${Variables.space100}) 100%,
        0 100%,
        ${Variables.space100} 50%
      )`;
    }
  }};
`;

export const StageItem = ({
  stage,
  index,
  currentStep,
  salesClosedStep,
  currentStatus,
  isFirst,
  isLast,
  onClick,
}: StageItemProps) => {
  const stageStatus = getStageStatus({
    index,
    stage,
    currentStatus,
    salesClosedStep,
    currentStep,
  });
  const stageLabel = translateStage(stage);

  return (
    <StageListItem>
      <Stage
        type="button"
        className="saleslead__element--focus-visible"
        isFirst={isFirst}
        isLast={isLast}
        status={stageStatus}
        onClick={onClick}
        disabled={!onClick}
        aria-current={stageStatus === "ACTIVE" ? "step" : undefined}
      >
        <Connector isFirst={isFirst} isLast={isLast} status={stageStatus}>
          <Text
            tagName="span"
            textStyle="Caption.M"
            color={getTextColor(stageStatus)}
            overflowHidden
          >
            {stageLabel}
          </Text>
        </Connector>
      </Stage>
    </StageListItem>
  );
};
